import React from 'react'
import clsx from 'clsx'
import {UIGameCard, UIGameCardProps} from '../UIGameCard/UIGameCard'
import styles from './UIGameCardStack.module.scss'

type StackDirection = 'horizontal' | 'vertical'

export interface UIGameCardStackProps extends UIGameCardProps {
    count?: number
    stackMax?: number
    stackDirection?: StackDirection
    gap?: number
    animate?: boolean
    hideBadge?: boolean
    showEmptyCard?: boolean
    numberOfCardsToGreyOut?: number
    tradeRatio?: number | undefined
    numberOfCardsToHighlight?: number
    numberOfCardsToSuggestion?: number
    zIndex?: number | undefined
    scaleOnHover?: boolean
}

const UIGameCardStackComponent = ({
    count = 1,
    stackMax = Infinity,
    stackDirection = 'horizontal',
    gap = -20,
    animate = true,
    hideBadge = false,
    showEmptyCard = false,
    className,
    numberOfCardsToGreyOut = 0,
    tradeRatio,
    numberOfCardsToHighlight = 0,
    numberOfCardsToSuggestion = 0,
    zIndex,
    scaleOnHover = false,
    ...cardProps
}: UIGameCardStackProps) => {
    const shouldShowEmptyCard = showEmptyCard && count == 0 && stackMax != 0
    const numberToAddToEffectiveCount = shouldShowEmptyCard ? 1 : 0
    const effectiveCount = Math.min(count, stackMax) + numberToAddToEffectiveCount

    const isVerticalDirection = stackDirection === 'vertical'
    const isHorizontalDirection = stackDirection === 'horizontal'

    return (
        <div
            className={clsx(styles.cardStackContainer, {
                [styles.vertical]: isVerticalDirection,
                [styles.scaleOnHover]: scaleOnHover,
            }, className)}
            style={{'--gap': `${gap}px`, ...(zIndex != undefined ? {zIndex: zIndex} : {})} as React.CSSProperties}
        >
            {Array.from({length: effectiveCount}).map((_, index) => {
                const isLast = index === effectiveCount - 1
                const showBadge = hideBadge ? false : (isHorizontalDirection || isLast)
                const badgeCount = showBadge ? (isLast ? count : index + 1) : undefined
                const shouldAnimate = animate && isHorizontalDirection
                const shouldGreyOut = index < numberOfCardsToGreyOut
                const countToShow = showBadge && shouldShowEmptyCard ? 0 : badgeCount
                const tradeRatioToShow = isLast ? tradeRatio : undefined
                const shouldHighlight = index < numberOfCardsToHighlight
                const shouldSuggestion = index < numberOfCardsToSuggestion
                return (
                    <div
                        className={clsx(styles.cardWrapper, {
                            [styles.animateIn]: shouldAnimate,
                        })}
                        key={index}
                        style={{'--index': index} as React.CSSProperties}
                    >
                        <UIGameCard
                            greyOut={shouldGreyOut}
                            highlightAnimation={shouldHighlight}
                            bounceAnimation={shouldSuggestion}
                            {...cardProps}
                            {...(countToShow !== undefined ? {count: countToShow} : {})}
                            {...(tradeRatioToShow !== undefined ? {tradeRatio: tradeRatioToShow} : {})}
                        />
                    </div>
                )
            })}
        </div>
    )
}

export const UIGameCardStack = React.memo(UIGameCardStackComponent)
